import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Botao,
  Campo,
  CampoDeSelecao,
  CelulaDeAcoes,
  EstadoCarregando,
  EstadoErro,
  EstadoVazio,
  MenuDeAcoes,
  ROTULO_STATUS_CONTRATO,
  STATUS_CONTRATO,
  SeloStatusContrato,
  Tabela,
  useDialogo,
  type ColunaTabela,
  type StatusContrato,
} from '../componentes/ui';
import { AvisoDoLote, BotaoDoLote, useAcaoEmLote } from '../componentes/AcaoEmLote';
import { useDesativarEmpresa } from '../hooks/useEscrita';
import { useEmpresaCorrente, useIdentidade } from '../hooks/useSessao';
import { useParticipacao } from '../hooks/useParticipacao';
import { assessoraEmAlgum } from '../servicos/participacao';
import { ErroDaApi } from './ErroDaApi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCarteira } from '../hooks/useOperacao';
import { BotaoExtrairXlsx } from '../componentes/BotaoExtrairXlsx';
import { desativarEmpresa } from '../servicos/escrita';
import { requisicaoAutenticada } from '../servicos/http';
import { resumirNomes } from '../servicos/lote';
import type { ColunaDaPlanilha } from '../servicos/planilha';
import { AdicionarPorSped } from './AdicionarPorSped';
import { BotaoAdicionarCliente } from './BotaoAdicionarCliente';
import type { PapelDaCarteira, ParteDaOperacao } from '../servicos/operacao';
import { porContrato } from './carteira-por-contrato';
import { formatarCnpj } from '../servicos/formato';
import { rotaDaEmpresa } from './origem-da-empresa';

/**
 * Listagem de uma carteira: Clientes, Fornecedores ou Assessores.
 *
 * ## Por que uma tela para os três
 *
 * As três respostas têm exatamente o mesmo formato, e as três telas fazem a
 * mesma coisa: listar, filtrar por nome ou CNPJ, abrir o detalhe. Três arquivos
 * quase idênticos seriam três lugares para corrigir a mesma coluna torta.
 *
 * O que muda entre elas é texto e destino do clique — e isso são parâmetros, não
 * arquitetura.
 *
 * ## O filtro é local, de propósito
 *
 * As três listas cabem inteiras na resposta (289, 48 e 3 itens) e o backend não
 * expõe busca nesses endpoints. Filtrar em memória evita uma requisição por
 * tecla digitada e responde instantaneamente. Quando a carteira crescer a ponto
 * de a lista não caber, o filtro sobe para o servidor — e o sinal disso será a
 * resposta paginada, não o número de itens na tela.
 */

interface Configuracao {
  readonly titulo: string;
  readonly descricao: string;
  readonly vazio: string;
  /** `null` quando a carteira ainda não tem tela de detalhe. */
  readonly rotaDeDetalhe: ((id: string) => string) | null;
}

const CONFIG: Readonly<Record<PapelDaCarteira, Configuracao>> = {
  clientes: {
    titulo: 'Clientes',
    descricao: 'Empresas que acumulam crédito de ICMS e o vendem através da operação.',
    vazio: 'Nenhuma empresa está cadastrada como cliente de ICMS.',
    rotaDeDetalhe: (id) => `/clientes/${id}`,
  },
  /*
   * Fornecedor e assessor abrem o **cadastro** da empresa, não um painel
   * próprio.
   *
   * O painel de participante existe (`PainelParticipante`), mas lê a empresa da
   * sessão — ele responde "como vai a minha operação", não "como vai a operação
   * daquela empresa". Apontar para lá exigiria um segundo hook por id, e o que a
   * linha de uma carteira mais utilmente abre é quem trabalha na empresa, que
   * documentos ela tem e para onde vai o pagamento.
   *
   * A rota carrega a origem (`?de=`) para o "Voltar" do cadastro devolver a
   * esta carteira, não à listagem geral de empresas.
   */
  fornecedores: {
    titulo: 'Fornecedores',
    descricao: 'Empresas que compram o crédito e emitem o certificado de transferência.',
    vazio: 'Nenhuma empresa está cadastrada como fornecedora.',
    rotaDeDetalhe: (id) => rotaDaEmpresa(id, 'fornecedores'),
  },
  assessores: {
    titulo: 'Assessores',
    descricao: 'Parceiros que indicam clientes e acompanham a operação.',
    vazio: 'Nenhuma empresa está cadastrada como assessora.',
    rotaDeDetalhe: (id) => rotaDaEmpresa(id, 'assessores'),
  },
};

export function Carteira(props: {
  readonly papel: PapelDaCarteira;
  /**
   * Título e descrição próprios — a carteira de clientes também é a tela de
   * Negociações do Recorrente: as empresas calculadas, com o contrato de cada
   * uma na etapa em que está.
   */
  readonly titulo?: string;
  readonly descricao?: string;
  /** A descrição para quem não é admin — a mesma tela, no escopo dele. */
  readonly descricaoDaParte?: string;
}): JSX.Element {
  const identidade = useIdentidade();
  const admin = identidade?.isAdmin === true;
  const config = {
    ...CONFIG[props.papel],
    ...(props.titulo === undefined ? {} : { titulo: props.titulo }),
    ...(props.descricao === undefined ? {} : { descricao: props.descricao }),
    ...(props.descricaoDaParte === undefined || admin ? {} : { descricao: props.descricaoDaParte }),
  };
  const empresa = useEmpresaCorrente();
  /*
    Quem não é admin vê a mesma tela, com a lista da empresa escolhida: os
    clientes que ela assessora, os que compram o crédito dela e ela mesma,
    quando é cliente. A API recusa empresa que não é da pessoa.
  */
  const consulta = useCarteira(props.papel, admin || empresa === null ? undefined : empresa);
  const participacao = useParticipacao();
  const assessora = !admin && assessoraEmAlgum(participacao.data);
  const [simulandoSped, setSimulandoSped] = useState(false);
  const navegar = useNavigate();
  const dialogo = useDialogo();
  const desativar = useDesativarEmpresa();
  const queryClient = useQueryClient();
  // O mesmo "Excluir" da linha (RN-306), para as empresas marcadas — só o admin.
  const excluirMarcadas = useAcaoEmLote<ParteDaOperacao>({
    acao: (l) => desativarEmpresa(l.businessId),
    nome: (l) => l.razaoSocial,
    aoTerminar: () => {
      void queryClient.invalidateQueries({ queryKey: ['empresas'] });
      void queryClient.invalidateQueries({ queryKey: ['carteira'] });
    },
  });
  const [busca, setBusca] = useState('');
  const [ano, setAno] = useState('');
  const [parametros, setParametros] = useSearchParams();

  /**
   * `?situacao=APROVACAO_ASSESSOR` — a carteira como lista de contratos por
   * etapa.
   *
   * As pendências do sino ("Contratos aguardando o assessor") apontavam para
   * `/contratos`, uma tela que foi removida; o clique caía no catch-all e
   * levava ao painel. A listagem de contratos parados numa etapa passou a ser
   * **esta** tela, filtrada pela situação do contrato: é aqui que o status de
   * cada cliente aparece, e é do perfil do cliente que a etapa é destravada.
   *
   * O filtro vem da URL, e não de um estado local, para o link do sino ser um
   * link — e para o operador poder guardá-lo. Só clientes têm contrato; nas
   * outras carteiras o parâmetro é ignorado. Um valor fora da lista de status
   * é tratado como ausente: filtrar por um status que não existe mostraria uma
   * lista vazia sem explicação.
   */
  const situacao: StatusContrato | null =
    props.papel === 'clientes' ? lerSituacao(parametros.get('situacao')) : null;
  const limparSituacao = (): void => {
    const proximos = new URLSearchParams(parametros);
    proximos.delete('situacao');
    setParametros(proximos, { replace: true });
  };

  /**
   * Filtrar por exercício é uma **consulta**, não um recorte da lista.
   *
   * A tela filtrava em memória por `anoContrato`, que é o ano do contrato
   * vigente — então escolher 2025 escondia todo cliente cuja operação mais
   * recente é de 2026, mesmo que ele tenha contrato em 2025. O sintoma era
   * concreto: 2025 devolvia **zero** clientes, e existem 7.
   *
   * `GET /icms/clients/ano/:ano` responde pelo exercício de verdade — quem tem
   * contrato do ano ou cálculo do ano anterior (RN-619); o segundo aparece como
   * "Sem contrato". A listagem sem ano continua sendo a carteira inteira.
   */
  /**
   * Fora do admin, o exercício pede de quem é a carteira: pela assessoria ou
   * pelo fornecedor (a API exige um dos dois). Quem é só cliente não tem
   * filtro de exercício — a lista é a empresa dele.
   */
  const papeisNoRecorrente = participacao.data?.recorrente ?? [];
  const filtroDoAno: string | null = admin
    ? ''
    : empresa === null
      ? null
      : assessora || papeisNoRecorrente.includes('ASSESSOR')
        ? `?parceiroId=${encodeURIComponent(empresa)}`
        : papeisNoRecorrente.includes('FORNECEDOR')
          ? `?fornecedorId=${encodeURIComponent(empresa)}`
          : null;

  const porAno = useQuery({
    queryKey: ['carteira-ano', props.papel, ano, filtroDoAno],
    queryFn: () =>
      requisicaoAutenticada<ParteDaOperacao[]>(`/icms/clients/ano/${ano}${filtroDoAno ?? ''}`),
    enabled: props.papel === 'clientes' && ano !== '' && filtroDoAno !== null,
  });

  /**
   * Exercícios oferecidos no filtro.
   *
   * Os anos que aparecem na carteira, os dois anteriores ao mais recente **e o
   * seguinte**: a listagem só conhece o contrato vigente de cada cliente, então
   * um exercício em que ninguém está hoje não apareceria na lista — e é
   * justamente ele que alguém quer consultar ao olhar para trás. O seguinte
   * entra porque o SPED deste ano alimenta a operação do próximo (RN-619): o
   * cliente que já mandou o SPED de 2026 é cliente de 2027, e o filtro de 2027
   * precisa existir antes de o primeiro contrato de 2027 ser fechado.
   */
  const anos = useMemo(() => {
    const daCarteira = (consulta.data ?? [])
      .map((i) => i.anoContrato)
      .filter((a): a is number => a !== null && a !== undefined);

    const recente = Math.max(new Date().getFullYear(), ...daCarteira, 0);
    return [...new Set([...daCarteira, recente + 1, recente, recente - 1, recente - 2])].sort(
      (a, b) => b - a,
    );
  }, [consulta.data]);

  const filtradas = useMemo(() => {
    // Com exercício escolhido, a fonte é a consulta por ano; sem ele, a carteira.
    let itens = ano === '' ? (consulta.data ?? []) : (porAno.data ?? []);

    // A situação do contrato recorta antes da busca: quem chegou pelo sino
    // quer "os contratos parados no assessor", e digitar um nome refina isso.
    if (situacao !== null) itens = porContrato(itens, situacao);

    const termo = busca.trim().toLowerCase();
    if (termo === '') return itens;

    // O CNPJ é comparado só em dígitos: quem digita "12.345" espera achar a
    // empresa cujo CNPJ começa com 12345, e o formato da tela não deve atrapalhar.
    const digitos = termo.replace(/\D/g, '');

    return itens.filter((i) => {
      const porNome =
        i.razaoSocial.toLowerCase().includes(termo) ||
        (i.nomeFantasia ?? '').toLowerCase().includes(termo);
      const porCnpj = digitos !== '' && i.cnpj.includes(digitos);
      return porNome || porCnpj;
    });
  }, [consulta.data, porAno.data, busca, ano, situacao]);

  /**
   * As colunas seguem o sistema antigo, que acertou o essencial: quem varre a
   * lista de clientes procura **status e fornecedor**, não só o nome. Fornecedor
   * e assessor só existem para clientes, então as colunas entram condicionadas.
   */
  const colunas: readonly ColunaTabela<ParteDaOperacao>[] = [
    ...(props.papel === 'clientes'
      ? [
          {
            chave: 'statusContrato',
            titulo: 'Contrato',
            larguraMinima: '10rem',
            celula: (l: ParteDaOperacao) =>
              l.statusContrato === null || l.statusContrato === undefined ? (
                <span className="text-sm text-conteudo-sutil">Sem contrato</span>
              ) : (
                <span className="flex items-center gap-2">
                  <SeloStatusContrato status={l.statusContrato as StatusContrato} />
                  {l.anoContrato !== null && l.anoContrato !== undefined && (
                    <span className="valor-fiscal text-xs text-conteudo-sutil">
                      {l.anoContrato}
                    </span>
                  )}
                </span>
              ),
          },
        ]
      : []),
    {
      chave: 'razaoSocial',
      titulo: 'Razão social',
      larguraMinima: '18rem',
      celula: (linha) => (
        <span className="flex flex-col">
          <span className="text-conteudo-primario">{linha.razaoSocial}</span>
          {linha.nomeFantasia !== null && linha.nomeFantasia !== linha.razaoSocial && (
            <span className="text-xs text-conteudo-sutil">{linha.nomeFantasia}</span>
          )}
        </span>
      ),
    },
    {
      chave: 'cnpj',
      titulo: 'CNPJ',
      larguraMinima: '11rem',
      celula: (linha) => (
        <span className="valor-fiscal whitespace-nowrap text-sm">{formatarCnpj(linha.cnpj)}</span>
      ),
    },
    ...(props.papel === 'clientes'
      ? [
          {
            chave: 'assessor',
            titulo: 'Assessor',
            larguraMinima: '11rem',
            celula: (l: ParteDaOperacao) =>
              l.assessor ?? <span className="text-conteudo-sutil">—</span>,
          },
          {
            chave: 'fornecedor',
            titulo: 'Fornecedor',
            larguraMinima: '13rem',
            celula: (l: ParteDaOperacao) =>
              l.fornecedor ?? <span className="text-conteudo-sutil">—</span>,
          },
        ]
      : []),
    /**
     * Item 7 de 15/09 — o "⋯" de cada linha. Excluir é o que o Deca 1 fazia:
     * desativa a empresa (RN-306), que sai de todas as carteiras e leva os
     * vínculos junto. Só o admin vê — é a mesma regra da tela de cadastro.
     */
    ...(admin
      ? [
          {
            chave: 'acoes',
            titulo: 'Ações',
            tituloOculto: true,
            larguraMinima: '3rem',
            celula: (l: ParteDaOperacao) => (
              <CelulaDeAcoes>
                <MenuDeAcoes
                  rotulo={l.razaoSocial}
                  itens={[
                    ...(config.rotaDeDetalhe === null
                      ? []
                      : [
                          {
                            rotulo: 'Abrir',
                            aoEscolher: () =>
                              navegar(
                                (config.rotaDeDetalhe as (id: string) => string)(l.businessId),
                              ),
                          },
                        ]),
                    {
                      rotulo: 'Excluir',
                      perigo: true,
                      descricao: 'Desativa a empresa e os vínculos dela.',
                      aoEscolher: () => {
                        void (async () => {
                          const ok = await dialogo.confirmar({
                            titulo: `Excluir ${l.razaoSocial}?`,
                            descricao:
                              'A empresa é desativada: sai das carteiras, os colaboradores perdem o vínculo e ela deixa de entrar em contrato. O cadastro e o histórico permanecem.',
                            rotuloConfirmar: 'Excluir',
                            tom: 'perigo',
                          });
                          if (ok) desativar.mutate(l.businessId);
                        })();
                      },
                    },
                  ]}
                />
              </CelulaDeAcoes>
            ),
          },
        ]
      : []),
  ];

  /*
    Empresa que no Recorrente é só cliente: "Negociações" é a negociação dela.
    Abre direto o perfil — o cálculo, o contrato, o cadastro —, em vez de uma
    lista com uma linha só. Quem assessora ou fornece vê a lista, como o admin.
  */
  if (!admin && props.papel === 'clientes') {
    if (participacao.isPending) {
      return (
        <Pagina titulo={config.titulo}>
          <EstadoCarregando contexto="as negociações" />
        </Pagina>
      );
    }
    const soCliente =
      participacao.isSuccess &&
      papeisNoRecorrente.includes('CLIENTE') &&
      !papeisNoRecorrente.includes('FORNECEDOR') &&
      !assessora;
    if (soCliente && empresa !== null) return <Navigate to={`/clientes/${empresa}`} replace />;
  }

  if (consulta.isError) {
    return (
      <Pagina titulo={config.titulo} descricao={config.descricao}>
        <EstadoErro
          titulo={`Não foi possível carregar ${config.titulo.toLowerCase()}`}
          mensagem={consulta.error.message}
          aoTentarNovamente={() => void consulta.refetch()}
        />
      </Pagina>
    );
  }

  const semResultado = !consulta.isLoading && filtradas.length === 0;

  return (
    <Pagina
      titulo={config.titulo}
      descricao={config.descricao}
      contagem={consulta.data?.length}
      acao={
        <div className="flex flex-wrap items-end gap-3">
          <Campo
            rotulo={`Buscar em ${config.titulo.toLowerCase()}`}
            rotuloOculto
            type="search"
            placeholder="Nome ou CNPJ"
            className="w-full sm:w-64"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          {/* Só clientes têm exercício — fornecedor e assessor não têm contrato próprio. */}
          {props.papel === 'clientes' && anos.length > 0 && filtroDoAno !== null && (
            <CampoDeSelecao
              rotulo="Exercício"
              className="w-36"
              value={ano}
              onChange={(e) => setAno(e.target.value)}
              opcoes={[
                { valor: '', rotulo: 'Todos' },
                ...anos.map((a) => ({ valor: String(a), rotulo: String(a) })),
              ]}
            />
          )}

          {/*
            A planilha leva a carteira **inteira** do exercício escolhido, não a
            busca nem a situação da tela: uma planilha que muda conforme o que
            está digitado ninguém consegue reproduzir depois. O escopo é o da
            listagem (RN-136/RN-137) — são as mesmas linhas que a tela recebeu.
          */}
          <BotaoExtrairXlsx
            titulo={ano === '' ? config.titulo : `${config.titulo} ${ano}`}
            disabled={(ano === '' ? consulta.data : porAno.data) === undefined}
            colunas={colunasDaPlanilha(props.papel)}
            linhas={(ano === '' ? consulta.data : porAno.data) ?? []}
          />
          {/* "+ Adicionar" só na carteira de clientes — ver `BotaoAdicionarCliente`. */}
          {props.papel === 'clientes' && admin && <BotaoAdicionarCliente />}
          {/* O assessor simula pelo SPED do contribuinte, sem gravar nada; se
            ele quiser seguir, "Cadastrar na minha carteira" traz o cliente. */}
          {props.papel === 'clientes' && assessora && (
            <Botao onClick={() => setSimulandoSped(true)}>Simular pelo SPED</Botao>
          )}
        </div>
      }
    >
      {/*
        O filtro por situação aparece como um chip removível, não como um
        seletor: ele vem de fora (do sino, de um link guardado), e a tela precisa
        dizer que está filtrada — senão a carteira "perdeu" clientes sem motivo
        aparente. O ×  devolve a carteira inteira.
      */}
      {situacao !== null && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-conteudo-secundario">
          <span>Mostrando só clientes com contrato em</span>
          <span className="inline-flex items-center gap-1 rounded-full border border-acento-ciano/50 bg-acento-ciano/10 py-0.5 pl-3 pr-1 text-sm font-medium text-acento-ciano">
            {ROTULO_STATUS_CONTRATO[situacao]}
            <button
              type="button"
              onClick={limparSituacao}
              aria-label={`Remover o filtro ${ROTULO_STATUS_CONTRATO[situacao]}`}
              className="flex h-5 w-5 items-center justify-center rounded-full transition-colors hover:bg-acento-ciano/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
            >
              <svg
                width={12}
                height={12}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
                aria-hidden
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </span>
        </div>
      )}

      {semResultado && busca.trim() !== '' ? (
        <EstadoVazio
          titulo="Nada encontrado"
          descricao={`Nenhum registro corresponde a "${busca.trim()}".`}
          acao={
            <Botao variante="secundario" onClick={() => setBusca('')}>
              Limpar busca
            </Botao>
          }
        />
      ) : semResultado && situacao !== null ? (
        <EstadoVazio
          titulo={`Nenhum contrato em ${ROTULO_STATUS_CONTRATO[situacao].toLowerCase()}`}
          descricao="Nenhum cliente da carteira está nessa etapa agora. A pendência do sino some quando o contrato avança."
          acao={
            <Botao variante="secundario" onClick={limparSituacao}>
              Ver a carteira inteira
            </Botao>
          }
        />
      ) : semResultado && !admin ? (
        <EstadoVazio
          titulo="Nenhuma negociação ainda"
          descricao={
            assessora
              ? 'Simule pelo SPED do contribuinte: o cálculo aparece sem cadastrar nada, e “Cadastrar na minha carteira” traz o cliente para esta lista.'
              : 'Quando a empresa tiver cálculo ou contrato no ICMS Recorrente, ele aparece aqui.'
          }
          {...(assessora
            ? {
                acao: <Botao onClick={() => setSimulandoSped(true)}>Simular pelo SPED</Botao>,
              }
            : {})}
        />
      ) : semResultado ? (
        <EstadoVazio titulo="Carteira vazia" descricao={config.vazio} />
      ) : (
        <>
          {desativar.isError && (
            <ErroDaApi titulo="Não foi possível excluir" erro={desativar.error} />
          )}
          <AvisoDoLote
            desfecho={excluirMarcadas.desfecho}
            feitos={(n) => (n === 1 ? '1 empresa excluída' : `${n} empresas excluídas`)}
            aoFechar={excluirMarcadas.limparDesfecho}
          />
          <Tabela
            legenda={admin ? `${config.titulo} da operação` : config.titulo}
            colunas={colunas}
            dados={filtradas}
            chaveDaLinha={(linha) => linha.chaveDaLinha ?? linha.businessId}
            chaveDeCabecalhoDaLinha="razaoSocial"
            carregando={consulta.isLoading}
            {...(config.rotaDeDetalhe !== null
              ? {
                  aoClicarNaLinha: (linha: ParteDaOperacao) =>
                    navegar((config.rotaDeDetalhe as (id: string) => string)(linha.businessId)),
                }
              : {})}
            {...(admin
              ? {
                  selecao: {
                    substantivo: { singular: 'empresa', plural: 'empresas', feminino: true },
                    nomeDaLinha: (l: ParteDaOperacao) => l.razaoSocial,
                    acoes: (marcadas: readonly ParteDaOperacao[], limpar: () => void) => (
                      <BotaoDoLote
                        rotulo="Excluir"
                        perigo
                        // Filtrada por etapa, a mesma empresa pode ocupar
                        // duas linhas (um contrato em cada): exclui-se uma vez.
                        marcadas={[...new Map(marcadas.map((l) => [l.businessId, l])).values()]}
                        limpar={limpar}
                        lote={excluirMarcadas}
                        confirmacao={(m) => ({
                          titulo:
                            m.length === 1
                              ? `Excluir ${m[0]!.razaoSocial}?`
                              : `Excluir ${m.length} empresas?`,
                          descricao: `${resumirNomes(m.map((l) => l.razaoSocial))}.\n\nCada uma é desativada: sai das carteiras, os colaboradores perdem o vínculo e ela deixa de entrar em contrato. O cadastro e o histórico permanecem.`,
                          rotuloConfirmar: 'Excluir',
                          tom: 'perigo',
                        })}
                      />
                    ),
                  },
                }
              : {})}
          />
        </>
      )}

      {assessora && empresa !== null && (
        <AdicionarPorSped
          aberto={simulandoSped}
          aoFechar={() => setSimulandoSped(false)}
          empresaDoAssessor={empresa}
        />
      )}
    </Pagina>
  );
}

/** Só aceita um status que a carteira conhece; qualquer outro é "sem filtro". */
function lerSituacao(valor: string | null): StatusContrato | null {
  if (valor === null) return null;
  return (STATUS_CONTRATO as readonly string[]).includes(valor) ? (valor as StatusContrato) : null;
}

/**
 * Cabeçalho padrão das telas internas.
 *
 * Exportado porque as outras telas usam o mesmo desenho — título, uma linha de
 * contexto e um espaço à direita para busca ou ação. Repetir a marcação em cada
 * tela faria os títulos divergirem em tamanho e espaçamento com o tempo.
 */
export function Pagina(props: {
  readonly titulo: string;
  readonly descricao?: string;
  readonly contagem?: number;
  readonly acao?: React.ReactNode;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    /*
      Largura inteira, não `max-w-7xl`.
      O teto de 1280px deixava faixas vazias dos dois lados em monitor grande, e
      as tabelas deste sistema são largas de verdade — cliente, CNPJ, assessor e
      fornecedor na mesma linha. O que precisa de teto é **texto corrido**, e a
      descrição já tem o seu (`max-w-3xl`): linha longa demais cansa de ler,
      tabela apertada esconde coluna.
    */
    <div className="flex w-full flex-col gap-6">
      {/* Faixa de título: dá ao topo da página um peso que o branco puro não tinha. */}
      <header className="-mx-4 -mt-8 flex flex-col gap-4 border-b border-borda bg-superficie-cabecalho px-4 py-7 sm:-mx-8 sm:flex-row sm:items-end sm:justify-between sm:px-8">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">
            {props.titulo}
            {props.contagem !== undefined && (
              <span className="ml-3 align-middle text-base font-normal text-conteudo-sutil">
                {props.contagem.toLocaleString('pt-BR')}
              </span>
            )}
          </h1>
          {props.descricao !== undefined && (
            <p className="mt-2 max-w-3xl text-sm text-conteudo-secundario">{props.descricao}</p>
          )}
        </div>
        {props.acao !== undefined && <div className="shrink-0">{props.acao}</div>}
      </header>

      {props.children}
    </div>
  );
}

/** As colunas da tabela, em valores de planilha; contrato, fornecedor e assessor só em clientes. */
function colunasDaPlanilha(papel: PapelDaCarteira): ColunaDaPlanilha<ParteDaOperacao>[] {
  return [
    { titulo: 'Razão social', largura: 46, valor: (l) => l.razaoSocial },
    { titulo: 'Nome fantasia', largura: 30, valor: (l) => l.nomeFantasia },
    { titulo: 'CNPJ', largura: 20, valor: (l) => formatarCnpj(l.cnpj) },
    ...(papel === 'clientes'
      ? ([
          {
            titulo: 'Contrato',
            largura: 22,
            valor: (l) =>
              l.statusContrato === null || l.statusContrato === undefined
                ? 'Sem contrato'
                : (ROTULO_STATUS_CONTRATO[l.statusContrato as StatusContrato] ?? l.statusContrato),
          },
          // Ano sem formato de número: "2.026" não é um ano.
          { titulo: 'Exercício', largura: 10, valor: (l) => l.anoContrato },
          { titulo: 'Fornecedor', largura: 40, valor: (l) => l.fornecedor },
          { titulo: 'Assessor', largura: 40, valor: (l) => l.assessor },
        ] satisfies ColunaDaPlanilha<ParteDaOperacao>[])
      : []),
  ];
}
